import * as React from 'react';

import { cn } from '@heidi/utils';

import {
  StatusAlert,
  StatusArchive,
  StatusClover,
  StatusDone,
  StatusDot,
  StatusEmpty,
  StatusIncircle,
  StatusInProgress,
  StatusInReview,
  StatusReady,
  StatusStar,
  StatusToDo,
} from '../icons';
import {
  glyphSizeClasses,
  markerColorClasses,
  semanticStatusClasses,
  statusIndicatorFrame,
} from './status-indicator.styles';
import type {
  StatusIndicatorMarker,
  StatusIndicatorProps,
  StatusIndicatorStatus,
} from './status-indicator.types';

type StatusGlyph = React.ForwardRefExoticComponent<
  React.SVGProps<SVGSVGElement> & React.RefAttributes<SVGSVGElement>
>;

const STATUS_GLYPHS: Record<StatusIndicatorStatus, StatusGlyph> = {
  empty: StatusEmpty,
  'to-do': StatusToDo,
  'in-progress': StatusInProgress,
  'in-review': StatusInReview,
  ready: StatusReady,
  done: StatusDone,
  alert: StatusAlert,
  archive: StatusArchive,
};

const MARKER_GLYPHS: Record<StatusIndicatorMarker, StatusGlyph> = {
  dot: StatusDot,
  clover: StatusClover,
  star: StatusStar,
  incircle: StatusIncircle,
};

const StatusIndicator = React.forwardRef<HTMLSpanElement, StatusIndicatorProps>(
  (
    {
      'data-testid': testId,
      className,
      size = 'sm',
      status,
      marker,
      markerColor,
      ...domProps
    },
    ref
  ) => {
    // Types enforce that exactly one of `status` / `marker` is supplied.
    const Glyph = marker ? MARKER_GLYPHS[marker] : STATUS_GLYPHS[status];
    const paletteClass = marker
      ? markerColorClasses[markerColor ?? 'neutral']
      : semanticStatusClasses[status];

    // When the caller supplies an accessible name the indicator carries meaning
    // on its own, so it must expose `role="img"` — `aria-label` on a role-less
    // span is prohibited ARIA (axe `aria-prohibited-attr`). Without a name it
    // stays decorative (no role; meaning comes from adjacent text). An
    // empty/whitespace `aria-label` (plausible from a dynamic value) is NOT a
    // name — promoting it to `role="img"` would be a nameless image (axe
    // `image-alt`), so it stays decorative too.
    const ariaLabel = domProps['aria-label'];
    const hasAccessibleName =
      (typeof ariaLabel === 'string' && ariaLabel.trim() !== '') ||
      domProps['aria-labelledby'] !== undefined;

    return (
      <span
        ref={ref}
        data-testid={testId}
        role={hasAccessibleName ? 'img' : undefined}
        className={cn(statusIndicatorFrame({ size }), paletteClass, className)}
        {...domProps}
      >
        <Glyph className={glyphSizeClasses[size]} />
      </span>
    );
  }
);
StatusIndicator.displayName = 'StatusIndicator';

export { StatusIndicator };
